import { useEffect, useRef, useState } from "react";
import { ActorState } from "../../models/ActorState";
import {
  addAttachmentToTake,
  removeAttachmentFromTake,
} from "../../app/slices/TrainingSlice";
import PiCreatorDropdown from "../../components/PiCreatorDropdown/PiCreatorDropdown";
import useAttachmentHelper from "../../hooks/AttachmentHelper.hook";
import PiCreatorDropdownItem from "../../components/PiCreatorDropdown/PiCreatorDropdownItem";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import { useDispatch } from "react-redux";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import DoDisturbAltOutlinedIcon from "@mui/icons-material/DoDisturbAltOutlined";
import usePropHelper from "../../hooks/PropHelper";
import ActorStateAttachmentTypeDropdown from "./ActorStateAttachmentTypeDropdown";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import ActorStateAddAttachmentButton from "./ActorStateAddAttachmentButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./ActorStatePropInfoField.css";

export default function ActorStatePropInfoField(props: {
  actorState: ActorState;
  variant?: "default" | "compact";
}) {
  const { actorState, variant = "default" } = { ...props };
  const { getAllAttachableProps, getPropsAttachedToActor } =
    useAttachmentHelper();
  const dispatch = useDispatch();
  const attachedProps = getPropsAttachedToActor(actorState.actorId);
  const { getUsedPropByInstanceId } = usePropHelper();
  const attachableProps = getAllAttachableProps();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const pendingExpandedPropIds = useRef<Set<string>>(new Set());
  const selectedPropIds = attachedProps.map(
    (attachedProp) => attachedProp.propPreset.id
  );
  const selectedPropIdsKey = selectedPropIds.join("|");

  useEffect(() => {
    setExpandedRows((current) => {
      const next: Record<string, boolean> = {};
      let changed = Object.keys(current).length !== selectedPropIds.length;

      selectedPropIds.forEach((propId) => {
        const shouldOpenPending = pendingExpandedPropIds.current.has(propId);
        next[propId] =
          current[propId] ?? (shouldOpenPending ? true : false);
        if (current[propId] !== next[propId]) changed = true;
        if (shouldOpenPending) {
          pendingExpandedPropIds.current.delete(propId);
        }
      });

      return changed ? next : current;
    });
  }, [selectedPropIdsKey]);

  const getDefaultTag = (propId: string) => {
    const usedProp = getUsedPropByInstanceId(propId);
    return usedProp.metadata.attachmentTags?.[0]?.tag ?? "";
  };

  const addAttachment = (propId: string, playerToProp = false) => {
    if (!propId) return;
    pendingExpandedPropIds.current.add(propId);

    dispatch(
      addAttachmentToTake({
        actorId: actorState.actorId,
        propId,
        tag: getDefaultTag(propId),
        playerToProp,
      })
    );
  };

  const removeAttachment = (propId: string) => {
    dispatch(
      removeAttachmentFromTake({
        actorId: actorState.actorId,
        propId,
      })
    );
  };

  const changeAttachmentProp = (
    currentPropId: string,
    nextPropId: string,
    playerToProp: boolean
  ) => {
    if (!nextPropId || currentPropId === nextPropId) return;

    removeAttachment(currentPropId);
    addAttachment(nextPropId, playerToProp);
  };

  const updateAttachmentTag = (
    propId: string,
    tag: string,
    playerToProp: boolean
  ) => {
    dispatch(
      addAttachmentToTake({
        actorId: actorState.actorId,
        propId,
        tag,
        playerToProp,
      })
    );
  };

  const updateAttachmentType = (
    propId: string,
    tag: string,
    playerToProp: boolean
  ) => {
    dispatch(
      addAttachmentToTake({
        actorId: actorState.actorId,
        propId,
        tag,
        playerToProp,
      })
    );
  };

  const getAttachmentOptions = (currentPropId?: string) => {
    return attachableProps.filter(
      (prop) => prop.id === currentPropId || !selectedPropIds.includes(prop.id)
    );
  };

  const toggleRow = (propId: string) => {
    setExpandedRows((current) => ({
      ...current,
      [propId]: !(current[propId] ?? false),
    }));
  };
  const addAttachmentOptions = getAttachmentOptions();

  const addAttachmentDropdown = (
    <PiCreatorDropdown
      label={"Add Attachment"}
      triggerChrome="none"
      trigger={
        <ActorStateAddAttachmentButton
          label="Select prop"
          icon={
            <AddCircleOutlineOutlinedIcon fontSize="inherit" color="inherit" />
          }
          disabled={addAttachmentOptions.length === 0}
          variant={variant === "compact" ? "compact" : "dashed"}
          data-testid="add-attachment-button"
        />
      }
      items={addAttachmentOptions.map((prop) => (
        <PiCreatorDropdownItem
          key={prop.id}
          text={prop.name}
          onClick={() => addAttachment(prop.id)}
          icon={<ViewInArOutlinedIcon fontSize="inherit" color="inherit" />}
        />
      ))}
    />
  );

  const rows =
    attachedProps.length === 0 ? (
      <div className="actorstatepropinfo-empty">
        <div className="actorstatepropinfo-emptyicon">
          <ViewInArOutlinedIcon fontSize="inherit" color="inherit" />
        </div>
        <div className="actorstatepropinfo-emptytitle">
          No attachments yet.
        </div>
        <div className="actorstatepropinfo-emptyhint">
          Attach props for this actor to hold or use.
        </div>
      </div>
    ) : (
      attachedProps.map((attachedProp) => {
        const availableProps = getAttachmentOptions(attachedProp.propPreset.id);
        const tagOptions = attachedProp.usedProp.metadata.attachmentTags ?? [];
        const isExpanded = expandedRows[attachedProp.propPreset.id] ?? false;
        const attachmentDirection = attachedProp.attachment.playerToProp
          ? "Player to Prop"
          : "Prop to Player";
        const detailsId = `attachment-details-${attachedProp.propPreset.id}`;
        const tagTrigger = attachedProp.attachment.tag ? (
          <PiCreatorDropdownItem
            text={attachedProp.attachment.tag}
            icon={<LocalOfferOutlinedIcon fontSize="inherit" color="inherit" />}
            onClick={() => {}}
          />
        ) : (
          <PiCreatorDropdownItem
            text="None"
            icon={<DoDisturbAltOutlinedIcon fontSize="inherit" color="inherit" />}
            onClick={() => {}}
          />
        );

        return (
          <div
            key={attachedProp.propPreset.id}
            className={`actorstatepropinfo-entry ${
              isExpanded ? "actorstatepropinfo-entry-open" : ""
            }`.trim()}
            data-testid={`attachment-row-${attachedProp.propPreset.id}`}
          >
            <div className="actorstatepropinfo-entryheader">
              <button
                type="button"
                className="actorstatepropinfo-entrysummary"
                data-testid={`attachment-row-toggle-${attachedProp.propPreset.id}`}
                aria-expanded={isExpanded}
                aria-controls={detailsId}
                onClick={() => toggleRow(attachedProp.propPreset.id)}
              >
                <div className="actorstatepropinfo-entryicon">
                  <ViewInArOutlinedIcon fontSize="inherit" color="inherit" />
                </div>
                <div className="actorstatepropinfo-entrycopy">
                  <div className="actorstatepropinfo-entrytitle">
                    {attachedProp.propPreset.name}
                  </div>
                  <div className="actorstatepropinfo-entrymeta">
                    <span className="actorstatepropinfo-tagpill">
                      <LocalOfferOutlinedIcon
                        fontSize="inherit"
                        color="inherit"
                      />
                      <span>{attachedProp.attachment.tag || "No tag"}</span>
                    </span>
                    <span>{attachmentDirection}</span>
                  </div>
                </div>
                <div
                  className={`actorstatepropinfo-entrychevron ${
                    isExpanded ? "actorstatepropinfo-entrychevron-open" : ""
                  }`}
                >
                  <ExpandMoreIcon fontSize="inherit" color="inherit" />
                </div>
              </button>
              <button
                type="button"
                aria-label={`Remove ${attachedProp.propPreset.name}`}
                className="actorstatepropinfo-removebutton"
                onClick={() => removeAttachment(attachedProp.propPreset.id)}
              >
                <CloseOutlinedIcon fontSize="inherit" color="inherit" />
              </button>
            </div>
            {isExpanded && (
              <div
                id={detailsId}
                className="actorstatepropinfo-entrydetails"
              >
                <div className="actorstatepropinfo-detailsection">
                  <PiCreatorDropdown
                    label={"Attachment"}
                    style={variant === "compact" ? "default" : "small"}
                    trigger={
                      <PiCreatorDropdownItem
                        text={attachedProp.propPreset.name}
                        icon={
                          <ViewInArOutlinedIcon
                            fontSize="inherit"
                            color="inherit"
                          />
                        }
                        onClick={() => {}}
                      />
                    }
                    items={availableProps.map((prop) => (
                      <PiCreatorDropdownItem
                        key={prop.id}
                        text={prop.name}
                        onClick={() =>
                          changeAttachmentProp(
                            attachedProp.propPreset.id,
                            prop.id,
                            attachedProp.attachment.playerToProp
                          )
                        }
                        icon={
                          <ViewInArOutlinedIcon
                            fontSize="inherit"
                            color="inherit"
                          />
                        }
                      />
                    ))}
                  />
                </div>
                <div className="actorstatepropinfo-detailsection">
                  <PiCreatorDropdown
                    label={"Tag"}
                    style={variant === "compact" ? "default" : "small"}
                    trigger={tagTrigger}
                    items={
                      tagOptions.length > 0
                        ? tagOptions.map((tag) => (
                            <PiCreatorDropdownItem
                              key={tag.tag}
                              text={tag.tag}
                              icon={
                                <LocalOfferOutlinedIcon
                                  fontSize="inherit"
                                  color="inherit"
                                />
                              }
                              onClick={() =>
                                updateAttachmentTag(
                                  attachedProp.propPreset.id,
                                  tag.tag,
                                  attachedProp.attachment.playerToProp
                                )
                              }
                            />
                          ))
                        : [
                            <PiCreatorDropdownItem
                              key="none"
                              text="None"
                              icon={
                                <DoDisturbAltOutlinedIcon
                                  fontSize="inherit"
                                  color="inherit"
                                />
                              }
                              onClick={() => {}}
                            />,
                          ]
                    }
                  />
                </div>
                <div className="actorstatepropinfo-detailsection">
                  <ActorStateAttachmentTypeDropdown
                    selected={attachedProp.attachment.playerToProp ?? false}
                    style={variant === "compact" ? "default" : "small"}
                    onChange={(playerToProp) =>
                      updateAttachmentType(
                        attachedProp.propPreset.id,
                        attachedProp.attachment.tag ?? "",
                        playerToProp
                      )
                    }
                  />
                </div>
              </div>
            )}
          </div>
        );
      })
    );

  return (
    <div
      className={[
        "actorstatepropinfo-root",
        variant === "compact" ? "actorstatepropinfo-root-compact" : "",
        attachedProps.length === 0 ? "actorstatepropinfo-root-empty" : "",
      ]
        .join(" ")
        .trim()}
    >
      <div
        className={`actorstatepropinfo-toolbar ${
          variant === "compact" ? "actorstatepropinfo-toolbar-compact" : ""
        }`.trim()}
      >
        {variant === "compact" && (
          <div className="border-t-[2px] border-solid border-creator-border w-full h-[6px]"></div>
        )}
        {addAttachmentDropdown}
      </div>
      <div
        className={`actorstatepropinfo-list pit-creator-scrollbar ${
          variant === "compact" ? "actorstatepropinfo-list-compact" : ""
        }`.trim()}
      >
        {rows}
      </div>
    </div>
  );
}
